import { useState, useEffect, useRef } from "react";
import { Play, Plus, Image, Youtube, Trash2, Calendar, MapPin, Phone, Mail, X, Camera, Film, Link, Upload, Star, Globe, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL - handles all URL formats
const getImageUrl = (url) => {
  if (!url) return '';
  
  // If it's a relative URL, prepend the backend URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  // If it's an old preview URL, replace with current backend URL
  if (url.includes('.preview.emergentagent.com') || url.includes('.emergent.host')) {
    // Extract the path after the domain
    const path = url.replace(/https?:\/\/[^\/]+/, '');
    return `${BACKEND_URL}${path}`;
  }
  
  // Otherwise return as is (external URLs)
  return url;
};

const YVOV_LOGO = "https://customer-assets.emergentagent.com/job_peace-alliance/artifacts/foyraiqv_7951.jpg";
const YVOV_FLYER = "https://customer-assets.emergentagent.com/job_peace-alliance/artifacts/j8g7donj_7953.jpg";

const workshopTopics = [
  "Gun Violence Prevention",
  "Sexual Assault Awareness",
  "Mental Health & Wellness",
  "Bullying Prevention",
  "Healthy Relationships",
  "Gang Involvement Impact",
  "Building Resilience",
  "Self-Love & Confidence"
];

export default function YouthVictory() {
  const { isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [addPhotoOpen, setAddPhotoOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState("upload");
  const [videoUploadMode, setVideoUploadMode] = useState("youtube");
  const [uploading, setUploading] = useState(false);
  const [newVideo, setNewVideo] = useState({ title: "", description: "", youtube_url: "", video_url: "" });
  const [newPhoto, setNewPhoto] = useState({ title: "", description: "", image_url: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const [videosRes, photosRes] = await Promise.all([
        axios.get(`${API}/yvov/videos`),
        axios.get(`${API}/yvov/photos`)
      ]);
      setVideos(videosRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const validFiles = [];
    const previews = [];
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file.`);
        continue;
      }
      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setPreviewUrls(previews);
      setSelectedFile(validFiles[0]);
      setPreviewUrl(previews[0]);
      toast.success(`${validFiles.length} photo(s) selected! Tap 'Upload Photos' to add them.`);
    }
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previewUrls];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
    if (newFiles.length > 0) {
      setSelectedFile(newFiles[0]);
      setPreviewUrl(newPreviews[0]);
    } else {
      setSelectedFile(null);
      setPreviewUrl(null);
    }
  };

  const handleVideoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error("Video is too large. Maximum size is 100MB.");
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      toast.success("Video selected! Add a title and tap 'Add Video'");
    }
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    
    if (uploadMode === "upload" && selectedFiles.length > 0) {
      setUploading(true);
      setUploadProgress(0);
      
      const uploadedPhotos = [];
      const totalFiles = selectedFiles.length;
      
      try {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const formData = new FormData();
          formData.append("file", file);
          formData.append("title", newPhoto.title || file.name.split('.')[0]);
          formData.append("description", newPhoto.description || "");
          
          const uploadRes = await axios.post(`${API}/upload/image`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000
          });
          
          const photoData = {
            title: newPhoto.title || file.name.split('.')[0],
            description: newPhoto.description,
            image_url: uploadRes.data.url
          };
          
          const response = await axios.post(`${API}/yvov/photos`, photoData);
          uploadedPhotos.push(response.data);
          
          setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
        }
        
        setPhotos([...uploadedPhotos.reverse(), ...photos]);
        resetPhotoForm();
        toast.success(`Successfully uploaded ${uploadedPhotos.length} photo(s)!`);
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(error.response?.data?.detail || "Failed to upload photos");
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    } else if (uploadMode === "upload" && selectedFile) {
      if (!newPhoto.title) {
        toast.error("Please enter a title");
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("title", newPhoto.title);
        formData.append("description", newPhoto.description || "");
        
        const uploadRes = await axios.post(`${API}/upload/image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000
        });
        
        const photoData = {
          title: newPhoto.title,
          description: newPhoto.description,
          image_url: uploadRes.data.url
        };
        
        const response = await axios.post(`${API}/yvov/photos`, photoData);
        setPhotos([response.data, ...photos]);
        resetPhotoForm();
        toast.success("Photo uploaded successfully!");
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(error.response?.data?.detail || "Failed to upload photo");
      } finally {
        setUploading(false);
      }
    } else {
      if (!newPhoto.title || !newPhoto.image_url) {
        toast.error("Please fill in required fields");
        return;
      }
      setUploading(true);
      try {
        const response = await axios.post(`${API}/yvov/photos`, newPhoto);
        setPhotos([response.data, ...photos]);
        resetPhotoForm();
        toast.success("Photo added successfully!");
      } catch (error) {
        toast.error("Failed to add photo");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleUploadVideo = async (e) => {
    e.preventDefault();
    if (videoUploadMode === "upload" && selectedFile) {
      if (!newVideo.title) {
        toast.error("Please enter a title");
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("title", newVideo.title);
        formData.append("description", newVideo.description || "");
        
        const uploadRes = await axios.post(`${API}/upload/video`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000
        });
        
        const videoData = {
          title: newVideo.title,
          description: newVideo.description,
          video_url: uploadRes.data.url,
          is_upload: true
        };
        
        const response = await axios.post(`${API}/yvov/videos`, videoData);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video uploaded successfully!");
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload video. File may be too large.");
      } finally {
        setUploading(false);
      }
    } else if (videoUploadMode === "url") {
      // Video URL mode (external video links)
      if (!newVideo.title || !newVideo.video_url) {
        toast.error("Please fill in title and video URL");
        return;
      }
      setUploading(true);
      try {
        const videoData = {
          title: newVideo.title,
          description: newVideo.description,
          video_url: newVideo.video_url,
          is_upload: true // Treat as upload since it's a direct video URL
        };
        const response = await axios.post(`${API}/yvov/videos`, videoData);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video added successfully!");
      } catch (error) {
        toast.error("Failed to add video");
      } finally {
        setUploading(false);
      }
    } else {
      // YouTube mode
      if (!newVideo.title || !newVideo.youtube_url) {
        toast.error("Please fill in required fields");
        return;
      }
      setUploading(true);
      try {
        const response = await axios.post(`${API}/yvov/videos`, newVideo);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video added successfully!");
      } catch (error) {
        toast.error("Failed to add video");
      } finally {
        setUploading(false);
      }
    }
  };

  const resetPhotoForm = () => {
    setNewPhoto({ title: "", description: "", image_url: "" });
    setSelectedFile(null);
    setSelectedFiles([]);
    setPreviewUrl(null);
    setPreviewUrls([]);
    setAddPhotoOpen(false);
    setUploadMode("upload");
    setUploadProgress(0);
  };

  const resetVideoForm = () => {
    setNewVideo({ title: "", description: "", youtube_url: "", video_url: "" });
    setSelectedFile(null);
    setPreviewUrl(null);
    setAddVideoOpen(false);
    setVideoUploadMode("youtube");
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await axios.delete(`${API}/yvov/videos/${videoId}`);
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success("Video deleted");
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/yvov/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleToggleVideoFeatured = async (videoId) => {
    try {
      const response = await axios.patch(`${API}/yvov/videos/${videoId}/feature`);
      setVideos(videos.map(v => 
        v.id === videoId ? { ...v, is_featured: response.data.is_featured } : v
      ));
      toast.success(response.data.is_featured ? "Video featured!" : "Video unfeatured");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  const handleTogglePhotoFeatured = async (photoId) => {
    try {
      const response = await axios.patch(`${API}/yvov/photos/${photoId}/feature`);
      setPhotos(photos.map(p => 
        p.id === photoId ? { ...p, is_featured: response.data.is_featured } : p
      ));
      toast.success(response.data.is_featured ? "Photo featured!" : "Photo unfeatured");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  // Sort items with featured first
  const sortedVideos = [...videos].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
  const sortedPhotos = [...photos].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
  const featuredCount = videos.filter(v => v.is_featured).length + photos.filter(p => p.is_featured).length;

  return (
    <div data-testid="youth-victory-page" className="bg-[#0F172A] min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <img 
                src={YVOV_LOGO} 
                alt="Youth Victory Over Violence Week Logo" 
                className="w-64 md:w-80 mx-auto lg:mx-0 mb-8"
              />
              <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
                Empowering Youth to Lead Change
              </h1>
              <p className="text-lg text-slate-300 mb-6">
                A weeklong event bringing together students, educators, and community leaders to raise awareness about issues affecting youth and inspire change through education, activism, and unity.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-2 text-orange-400">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">April 27th - May 1st</span>
                </div>
                <div className="flex items-center gap-2 text-blue-400">
                  <MapPin className="w-5 h-5" />
                  <span>Dr. Howard Fuller Collegiate Academy</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src={YVOV_FLYER} 
                alt="Youth Victory Over Violence Week Flyer" 
                className="rounded-2xl shadow-2xl w-full max-w-lg mx-auto border-4 border-orange-500/30"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Workshop Topics */}
      <section className="py-16 bg-gradient-to-b from-[#0F172A] to-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white text-center mb-10">
            Workshop Topics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {workshopTopics.map((topic, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-colors"
              >
                <span className="text-white font-medium">{topic}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Gallery */}
      <section className="py-16 bg-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
            <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white">
              Media Gallery
            </h2>
            <div className="flex gap-3">
              {/* Add Video Dialog */}
              <Dialog open={addVideoOpen} onOpenChange={(open) => { if (!open) resetVideoForm(); else setAddVideoOpen(true); }}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="add-yvov-video-btn">
                    <Film className="w-4 h-4 mr-2" />
                    Add Video
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] text-white">Add Video</DialogTitle>
                  </DialogHeader>
                  
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      type="button"
                      variant={videoUploadMode === "youtube" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "youtube" ? "bg-orange-500 text-white" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("youtube"); setSelectedFile(null); setPreviewUrl(null); }}
                    >
                      <Youtube className="w-4 h-4 mr-2" />
                      YouTube
                    </Button>
                    <Button
                      type="button"
                      variant={videoUploadMode === "url" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "url" ? "bg-orange-500 text-white" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("url"); setSelectedFile(null); setPreviewUrl(null); setNewVideo({ ...newVideo, youtube_url: "" }); }}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Video URL
                    </Button>
                    <Button
                      type="button"
                      variant={videoUploadMode === "upload" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "upload" ? "bg-orange-500 text-white" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("upload"); setNewVideo({ ...newVideo, youtube_url: "", video_url: "" }); }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>

                  <form onSubmit={handleUploadVideo} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-300">Title *</Label>
                      <Input
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="Video title"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        required
                      />
                    </div>

                    {videoUploadMode === "youtube" && (
                      <div>
                        <Label className="text-slate-300">YouTube URL *</Label>
                        <Input
                          value={newVideo.youtube_url}
                          onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                        <p className="text-xs text-slate-400 mt-1">Paste a YouTube video link</p>
                      </div>
                    )}

                    {videoUploadMode === "url" && (
                      <div>
                        <Label className="text-slate-300">Video URL *</Label>
                        <Input
                          value={newVideo.video_url}
                          onChange={(e) => setNewVideo({ ...newVideo, video_url: e.target.value })}
                          placeholder="https://example.com/video.mp4"
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                        <p className="text-xs text-slate-400 mt-1">Direct link to video file (MP4, WebM, etc.) or embed URL</p>
                      </div>
                    )}

                    {videoUploadMode === "upload" && (
                      <div>
                        <Label className="text-slate-300">Select Video *</Label>
                        <input
                          ref={videoFileInputRef}
                          type="file"
                          accept="video/*"
                          capture="environment"
                          onChange={handleVideoFileSelect}
                          className="hidden"
                          id="yvov-video-upload"
                        />
                        <label 
                          htmlFor="yvov-video-upload"
                          className="mt-2 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-orange-700 touch-manipulation"
                        >
                          <Film className="w-5 h-5" />
                          {selectedFile ? "Change Video" : "Choose Video from Phone"}
                        </label>
                        {previewUrl && (
                          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
                            <video src={previewUrl} className="max-h-32 mx-auto rounded-lg" controls />
                            <p className="text-sm text-slate-400 text-center mt-2">{selectedFile?.name}</p>
                            <p className="text-xs text-green-400 text-center mt-1">✓ Video selected</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-slate-300">Description</Label>
                      <Textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                        placeholder="Brief description"
                        className="mt-1 bg-slate-800 border-slate-600 text-white resize-none"
                        rows={2}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-orange-500 hover:bg-orange-600" 
                      disabled={uploading}
                    >
                      {uploading ? "Uploading..." : "Add Video"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Add Photo Dialog */}
              <Dialog open={addPhotoOpen} onOpenChange={(open) => { if (!open) resetPhotoForm(); else setAddPhotoOpen(true); }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-500 hover:bg-blue-600 text-white rounded-full" data-testid="add-yvov-photo-btn">
                    <Camera className="w-4 h-4 mr-2" />
                    Add Photo
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] text-white">Add Photo</DialogTitle>
                  </DialogHeader>
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      type="button"
                      variant={uploadMode === "upload" ? "default" : "outline"}
                      className={`flex-1 ${uploadMode === "upload" ? "bg-blue-500" : "border-slate-600 text-white"}`}
                      onClick={() => { setUploadMode("upload"); setNewPhoto({ ...newPhoto, image_url: "" }); }}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      From Phone
                    </Button>
                    <Button
                      type="button"
                      variant={uploadMode === "url" ? "default" : "outline"}
                      className={`flex-1 ${uploadMode === "url" ? "bg-blue-500" : "border-slate-600 text-white"}`}
                      onClick={() => { setUploadMode("url"); setSelectedFile(null); setSelectedFiles([]); setPreviewUrl(null); setPreviewUrls([]); }}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Image URL
                    </Button>
                  </div>

                  <form onSubmit={handleUploadPhoto} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-300">Title (optional for multiple photos)</Label>
                      <Input
                        value={newPhoto.title}
                        onChange={(e) => setNewPhoto({ ...newPhoto, title: e.target.value })}
                        placeholder="Photo title (or leave blank to use filename)"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>

                    {uploadMode === "upload" ? (
                      <div>
                        <Label className="text-slate-300">Select Photos *</Label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                          id="yvov-photo-upload"
                        />
                        <label 
                          htmlFor="yvov-photo-upload"
                          className="mt-2 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-blue-800 touch-manipulation"
                        >
                          <Camera className="w-5 h-5" />
                          {selectedFiles.length > 0 ? `${selectedFiles.length} Photo(s) Selected - Tap to Add More` : "Choose Photos from Phone"}
                        </label>
                        {previewUrls.length > 0 && (
                          <div className="mt-4 p-3 bg-slate-800 rounded-xl">
                            <div className="grid grid-cols-4 gap-1 mb-2 max-h-24 overflow-hidden">
                              {previewUrls.slice(0, 8).map((url, index) => (
                                <div key={index} className="relative">
                                  <img src={url} alt={`Preview ${index + 1}`} className="w-full h-12 object-cover rounded" />
                                  <button
                                    type="button"
                                    onClick={() => removeSelectedFile(index)}
                                    className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"
                                  >
                                    <X className="w-2 h-2 text-white" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            {previewUrls.length > 8 && (
                              <p className="text-xs text-slate-400 text-center">+{previewUrls.length - 8} more photos</p>
                            )}
                            <p className="text-xs text-green-400 text-center">✓ {selectedFiles.length} photo(s) ready</p>
                            {uploading && uploadProgress > 0 && (
                              <div className="mt-2">
                                <div className="w-full bg-slate-700 rounded-full h-2">
                                  <div className="bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                                </div>
                                <p className="text-xs text-center text-slate-400 mt-1">Uploading... {uploadProgress}%</p>
                              </div>
                            )}
                          </div>
                        )}
                        {previewUrls.length === 0 && (
                          <p className="text-xs text-slate-400 text-center mt-2">Select multiple photos • Max 10MB each</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <Label className="text-slate-300">Image URL *</Label>
                        <Input
                          value={newPhoto.image_url}
                          onChange={(e) => setNewPhoto({ ...newPhoto, image_url: e.target.value })}
                          placeholder="https://..."
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                    )}

                    {/* Upload Button - Always visible */}
                    <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg font-bold" disabled={uploading || (uploadMode === "upload" && selectedFiles.length === 0)}>
                      {uploading ? `Uploading... ${uploadProgress}%` : selectedFiles.length > 1 ? `📤 Upload ${selectedFiles.length} Photos` : "📤 Upload Photo"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <Tabs defaultValue="videos" className="w-full">
            <TabsList className="bg-slate-800 border border-slate-700 mb-8">
              <TabsTrigger value="videos" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                <Youtube className="w-4 h-4 mr-2" />
                Videos ({videos.length})
              </TabsTrigger>
              <TabsTrigger value="photos" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                <Image className="w-4 h-4 mr-2" />
                Photos ({photos.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="videos">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner w-10 h-10 border-orange-500" />
                </div>
              ) : videos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedVideos.map((video) => (
                    <div key={video.id} className={`bg-slate-800 rounded-xl overflow-hidden group hover:ring-2 hover:ring-orange-500 transition-all ${video.is_featured ? 'ring-2 ring-amber-400' : ''}`}>
                      <div className="relative aspect-video bg-slate-900 cursor-pointer" onClick={() => setSelectedVideo(video)}>
                        {/* Featured Badge */}
                        {video.is_featured && (
                          <div className="absolute top-2 left-2 z-10 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Star className="w-3 h-3" fill="currentColor" />
                            Featured
                          </div>
                        )}
                        {video.is_upload ? (
                          video.video_url?.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                            <video src={getImageUrl(video.video_url)} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-slate-800 flex flex-col items-center justify-center">
                              <Globe className="w-12 h-12 text-slate-500 mb-2" />
                              <span className="text-xs text-slate-500">External Video</span>
                            </div>
                          )
                        ) : (
                          <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" onError={(e) => { e.target.src = "https://via.placeholder.com/640x360/1e293b/f97316?text=Video"; }} />
                        )}
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                            <Play className="w-8 h-8 text-white ml-1" />
                          </div>
                        </div>
                        {/* Action buttons */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); handleToggleVideoFeatured(video.id); }} className={`p-1.5 rounded-full transition-colors ${video.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-black/50 text-white hover:bg-amber-400 hover:text-amber-900'}`}>
                            <Star className="w-4 h-4" fill={video.is_featured ? "currentColor" : "none"} />
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex justify-between items-start gap-2">
                          <h3 className="font-semibold text-white line-clamp-2">{video.title}</h3>
                          <button onClick={() => handleDeleteVideo(video.id)} className="p-1 text-slate-500 hover:text-red-500 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-slate-800/50 rounded-2xl">
                  <Youtube className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Videos Yet</h3>
                  <p className="text-slate-400 mb-6">Add videos from Youth Victory Over Violence Week events.</p>
                  <Button onClick={() => setAddVideoOpen(true)} className="bg-orange-500 hover:bg-orange-600">
                    <Plus className="w-5 h-5 mr-2" />
                    Add First Video
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="photos">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner w-10 h-10 border-blue-500" />
                </div>
              ) : photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {sortedPhotos.map((photo) => (
                    <div key={photo.id} className={`relative aspect-square bg-slate-800 rounded-xl overflow-hidden group cursor-pointer ${photo.is_featured ? 'ring-2 ring-amber-400' : ''}`} onClick={() => setSelectedPhoto(photo)}>
                      {/* Featured Badge */}
                      {photo.is_featured && (
                        <div className="absolute top-2 left-2 z-10 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Star className="w-3 h-3" fill="currentColor" />
                          Featured
                        </div>
                      )}
                      <img src={getImageUrl(photo.image_url)} alt={photo.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <h3 className="text-white font-medium text-sm line-clamp-2">{photo.title}</h3>
                        </div>
                      </div>
                      {/* Action buttons */}
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); handleTogglePhotoFeatured(photo.id); }} className={`p-1.5 rounded-full transition-colors ${photo.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-black/50 text-white hover:bg-amber-400 hover:text-amber-900'}`}>
                          <Star className="w-4 h-4" fill={photo.is_featured ? "currentColor" : "none"} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }} className="p-1.5 bg-black/50 rounded-full text-white hover:bg-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-slate-800/50 rounded-2xl">
                  <Image className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Photos Yet</h3>
                  <p className="text-slate-400 mb-6">Add photos from Youth Victory Over Violence Week events.</p>
                  <Button onClick={() => setAddPhotoOpen(true)} className="bg-blue-500 hover:bg-blue-600">
                    <Plus className="w-5 h-5 mr-2" />
                    Add First Photo
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-b from-[#1E293B] to-[#0F172A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-6">Get Involved</h2>
          <p className="text-slate-300 mb-8">Want to participate or bring Youth Victory Over Violence Week to your school? Contact us!</p>
          <div className="flex flex-wrap justify-center gap-6">
            <a href="tel:414-502-7296" className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full transition-colors">
              <Phone className="w-5 h-5" />
              414-502-7296
            </a>
            <a href="mailto:tracey.e.dent@gmail.com" className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full transition-colors">
              <Mail className="w-5 h-5" />
              tracey.e.dent@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 bg-[#0F172A] border-slate-700 overflow-hidden">
          <DialogTitle className="sr-only">{selectedVideo?.title}</DialogTitle>
          {selectedVideo && (
            <div>
              {selectedVideo.is_upload ? (
                selectedVideo.video_url?.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                  <video src={getImageUrl(selectedVideo.video_url)} controls autoPlay className="w-full max-h-[70vh]" />
                ) : (
                  <div className="aspect-video bg-slate-900 flex flex-col items-center justify-center p-8">
                    <Globe className="w-16 h-16 text-slate-500 mb-4" />
                    <p className="text-slate-400 text-center mb-6">This video is hosted on an external website</p>
                    <a href={selectedVideo.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition-colors">
                      <ExternalLink className="w-5 h-5" />
                      Watch on External Site
                    </a>
                  </div>
                )
              ) : (
                <div className="youtube-container">
                  <iframe src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1`} title={selectedVideo.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedVideo.title}</h3>
                {selectedVideo.description && <p className="text-slate-400">{selectedVideo.description}</p>}
                {selectedVideo.is_upload && selectedVideo.video_url && !selectedVideo.video_url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) && (
                  <a href={selectedVideo.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-orange-400 hover:text-orange-300 mt-3 text-sm">
                    {selectedVideo.video_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 bg-[#0F172A] border-slate-700 overflow-hidden">
          <DialogTitle className="sr-only">{selectedPhoto?.title}</DialogTitle>
          {selectedPhoto && (
            <div>
              <img src={getImageUrl(selectedPhoto.image_url)} alt={selectedPhoto.title} className="w-full max-h-[70vh] object-contain bg-black" />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedPhoto.title}</h3>
                {selectedPhoto.description && <p className="text-slate-400">{selectedPhoto.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
